"use strict";
/**
 * Period maths behind Energy Usage Trends (mobile card, hub cards): which dates a Day / Week /
 * Month covers, what it is compared with, and the date and axis labels both apps print.
 * Dates are device-local `YYYY-MM-DD`; hour keys are `YYYY-MM-DD-HH` (see costSeries).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatEnergyValue = exports.niceMax = exports.tipDate = exports.barLabel = exports.periodLabel = exports.rangeLabel = exports.shortDate = exports.sameStretchEnd = exports.changePercent = exports.MIN_COMPARE_KWH = exports.MIN_COMPARE_HOURS = exports.comparePeriodStart = exports.shiftPeriod = exports.periodRange = exports.daysBetween = exports.addDays = void 0;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_LABELS = { 0: '12a', 6: '6a', 12: '12p', 18: '6p' };
const pad = (n) => String(n).padStart(2, '0');
const parseDate = (date) => {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
};
const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const addDays = (date, delta) => {
    const next = parseDate(date);
    next.setDate(next.getDate() + delta);
    return toDateString(next);
};
exports.addDays = addDays;
/** Whole days from `from` to `to`. Noon avoids DST hour shifts. */
const daysBetween = (from, to) => Math.round((new Date(`${to}T12:00:00`).getTime() - new Date(`${from}T12:00:00`).getTime()) / 86_400_000);
exports.daysBetween = daysBetween;
/** Date range of the Day / Week (Sunday-aligned) / calendar Month containing `anchor`. */
const periodRange = (mode, anchor) => {
    if (mode === 'day')
        return { start: anchor, end: anchor };
    if (mode === 'week') {
        const start = (0, exports.addDays)(anchor, -parseDate(anchor).getDay());
        return { start, end: (0, exports.addDays)(start, 6) };
    }
    const [year, month] = anchor.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${pad(lastDay)}` };
};
exports.periodRange = periodRange;
/** Start of the period `delta` periods away from the one starting at `start`. */
const shiftPeriod = (mode, start, delta) => {
    if (mode === 'day')
        return (0, exports.addDays)(start, delta);
    if (mode === 'week')
        return (0, exports.addDays)(start, 7 * delta);
    const [year, month] = start.split('-').map(Number);
    return toDateString(new Date(year, month - 1 + delta, 1));
};
exports.shiftPeriod = shiftPeriod;
/**
 * What a period is compared against: the same day LAST WEEK on Day (weekday vs weekday;
 * Monday vs Sunday says nothing), else the previous week / month.
 */
const comparePeriodStart = (mode, start) => mode === 'day' ? (0, exports.addDays)(start, -7) : (0, exports.shiftPeriod)(mode, start, -1);
exports.comparePeriodStart = comparePeriodStart;
/** Below this many hours of today, a same-stretch % is noise (matches the old Today card). */
exports.MIN_COMPARE_HOURS = 3;
/** Less than this in the compared period gets no %: a change from ~0 is meaningless. */
exports.MIN_COMPARE_KWH = 0.01;
/** % change, or null when the earlier value is too small to compare against. */
const changePercent = (current, earlier) => earlier >= exports.MIN_COMPARE_KWH ? ((current - earlier) / earlier) * 100 : null;
exports.changePercent = changePercent;
/**
 * End of the stretch of `compared` that matches a period still running: as many days in as
 * today is, that last day cut to the hours today has data for — so a half-finished day or
 * month doesn't always read "less". No `endHours` = the whole compared period, which is also
 * what a longer current period gets (Oct 31 vs a 30-day September).
 */
const sameStretchEnd = (currentStart, compared, todayDate, todayHoursWithData) => {
    const end = (0, exports.addDays)(compared.start, (0, exports.daysBetween)(currentStart, todayDate));
    return end > compared.end ? { end: compared.end } : { end, endHours: todayHoursWithData };
};
exports.sameStretchEnd = sameStretchEnd;
const shortDate = (date) => {
    const [, month, day] = date.split('-').map(Number);
    return `${MONTHS[month - 1]} ${day}`;
};
exports.shortDate = shortDate;
/** "Oct 11 – 17" inside one month, "Sep 27 – Oct 3" across two. */
const rangeLabel = (start, end) => start.slice(0, 7) === end.slice(0, 7)
    ? `${(0, exports.shortDate)(start)} – ${Number(end.slice(8))}`
    : `${(0, exports.shortDate)(start)} – ${(0, exports.shortDate)(end)}`;
exports.rangeLabel = rangeLabel;
/** "Oct 21" / "Oct 18 – 24" / "Oct 2026". The apps add their own translated "Today, …". */
const periodLabel = (mode, start, end) => {
    if (mode === 'day')
        return (0, exports.shortDate)(start);
    if (mode === 'week')
        return (0, exports.rangeLabel)(start, end);
    const [year, month] = start.split('-').map(Number);
    return `${MONTHS[month - 1]} ${year}`;
};
exports.periodLabel = periodLabel;
/** X-axis label for a bar: hours on Day, weekdays on Week, every 5th day on Month. */
const barLabel = (mode, key, index) => {
    if (mode === 'day')
        return HOUR_LABELS[index] ?? '';
    if (mode === 'week')
        return WEEKDAYS[parseDate(key).getDay()];
    const day = index + 1;
    return day === 1 || day % 5 === 0 ? String(day) : '';
};
exports.barLabel = barLabel;
/** "Oct 16" for a day key, "Oct 20, 3 PM" for an hour key. */
const tipDate = (key) => {
    if (key.length <= 10)
        return (0, exports.shortDate)(key);
    const hour = Number(key.slice(11));
    return `${(0, exports.shortDate)(key.slice(0, 10))}, ${hour % 12 || 12} ${hour < 12 ? 'AM' : 'PM'}`;
};
exports.tipDate = tipDate;
/** Round max up to a step of 1/2/2.5/5 x 10^n so the `sections` gridlines land on tidy values. */
const niceMax = (values, sections) => {
    const max = Math.max(0, ...values);
    if (max <= 0)
        return 1;
    const step = max / sections;
    const magnitude = 10 ** Math.floor(Math.log10(step));
    const nice = [1, 2, 2.5, 5, 10].find((n) => n * magnitude >= step) ?? 10;
    return nice * magnitude * sections;
};
exports.niceMax = niceMax;
/**
 * "$1,015.17" / "1,015.17 kWh" (toFixed alone printed "$1015.17"). Two decimals on kWh too:
 * with one, a quiet circuit's hour (0.04 kWh) read "0.0 kWh".
 */
const formatEnergyValue = (unit, value) => {
    const number = value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return unit === 'cost' ? `$${number}` : `${number} kWh`;
};
exports.formatEnergyValue = formatEnergyValue;
